import React, {
    useEffect,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';

import { useHistory } from 'react-router-dom';

import Head from 'bdvp-common/src/components/Head';
import ContentCardsGroup from 'bdvp-common/src/components/ContentCardsGroup';
import PageTemplate from 'bdvp-common/src/components/PageTemplate';
import PageHeading from 'bdvp-common/src/components/PageHeading';
import Error from 'bdvp-common/src/components/Error';
import { cardContexts } from 'bdvp-common/src/components/Card';
import { getMediaPath, scrollRestore } from 'bdvp-common/src/utils';
import {
    useDidUpdate,
    usePagination,
    useMainContainerQuery,
} from 'bdvp-common/src/hooks';
import { Tracking } from 'bdvp-tracking';

import DeleteModal from '../../containers/DeleteModal';
import EmptyReelsPage from './EmptyReelsPage';

const ReelsPage = (props) => {

    const {
        userId,
        error,
        loading,
        data,
        loadingMore,
        fetchMoreData,
        deleteReelIndex,
    } = props;


    const history = useHistory();
    const total = data.getReels?.total ?? 0;
    const reels = useMemo(() => {

        return data.getReels?.reels || [];

    }, [data]);

    const list = useMemo(() => {

        return reels.map((item) => (item.mediaPlayback || {}));

    }, [reels]);

    const hasMore = list.length < total;

    const { firstName, nickName } = data.userInfo || {};
    const title = (nickName || firstName || userId) + "'s Reels";

    const { REEL_PAGE: CARD_CONTEXT } = cardContexts;

    const reelFocusIndex = React.useRef(null);
    const searchButtonRef = React.useRef(null);

    useEffect(() => {

        Tracking.trackReelsPageView();

    }, []);

    useEffect(() => {

        scrollRestore();

    }, [userId]);

    const {
        limit,
        sizes,
    } = useMainContainerQuery(
        [
            'XL',
            'LG',
            'MD',
            'SM',
            'XS',
        ],
        [
            {
                limit: 20,
                sizes: {
                    xs: 20,
                },
            },
            {
                limit: 16,
                sizes: {
                    xs: 25,
                },
            },
            {
                limit: 15,
                sizes: {
                    xs: 33.33,
                },
            },
            {
                limit: 12,
                sizes: {
                    xs: 50,
                },
            },
            {
                limit: 6,
                sizes: {
                    xs: 100,
                },
            },
        ],
        {
            limit: 20,
            sizes: {
                xs: 100,
                sm: 50,
                md: 33.33,
                lg: 25,
                xl: 20,
            },
        }
    );

    const {
        paginatedList,
        hasMoreItems,
        getMoreItems,
        resetPagination,
        loadingPagination: isLoading,
    } = usePagination({
        list,
        limit,
        hasMore,
        fetchMore: fetchMoreData,
        isLoading: loading,
    });

    useDidUpdate(() => {

        resetPagination();

    }, [userId]);

    const onItemClick = (event, item, index) => {

        event.preventDefault();
        const mediaPath = getMediaPath(item);

        history.push({
            pathname: `/${mediaPath}`,
        });

        Tracking.trackReelsPageOnItemClick(event, index);

    };

    const loadMoreButtonHandler = () => {

        getMoreItems();
        Tracking.trackReelsPageSeeMoreButtonClick();

    };

    const onDeleteReelHandler = () => {

        if(list?.length === 0) {

            searchButtonRef.current.focus();

        } else {

            reelFocusIndex.current = 0;

        }

    };

    const onCloseDeleteModalHandler = () => {

        reelFocusIndex.current = deleteReelIndex;

    };

    if (error) {

        return <Error />;

    }

    if (!isLoading && reels?.length === 0) {

        return (
            <>
                <Head title={title} />
                <EmptyReelsPage searchButtonRef={searchButtonRef} />
            </>
        );

    }

    return (
        <>
            <Head title={title} />
            <PageTemplate
                header={<PageHeading title={title} isLoading={isLoading} />}
                body={
                    <ContentCardsGroup
                        cardContext={CARD_CONTEXT}
                        handleItemClick={onItemClick}
                        hasMore={hasMoreItems}
                        isLoading={isLoading}
                        loadingMore={loadingMore}
                        limit={limit}
                        list={paginatedList}
                        loadMoreButtonHandler={loadMoreButtonHandler}
                        orientation={'portrait'}
                        cardFocusIndex={reelFocusIndex.current}
                        sizes={sizes}
                    />
                }
            />
            <DeleteModal
                onClose={onCloseDeleteModalHandler}
                onDelete={onDeleteReelHandler}
            />
        </>

    );

};

ReelsPage.propTypes = {
    userId: PropTypes.string.isRequired,
    loadingMore: PropTypes.bool.isRequired,
    fetchMoreData: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    data: PropTypes.object,
    error: PropTypes.object,
};

ReelsPage.defaultProps = {
    error: undefined,
    data: {},
};
export default ReelsPage;


